import { FilterType, FilterValue } from '@/models';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { Filter, LogicalOrFilter } from '@cubejs-client/core';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useQuery from '../useQuery';

import { HCP_CUBE_NAME } from '@/api/hcp/interaction/types';
import { toNumberOrNull, useAllowlistedGlobalFilters } from '@/utils/cube';

import { CubeQuery, QueryBaseOptions } from '@/types/cube';
import { HCPKeys } from './cacheKeys';

interface TotalCountQueryOptions extends QueryBaseOptions {
  globalFilterApplied?: boolean;
}

type UseGetHcpTotalCountRequest = {
  hcpBks?: string[];
  additionalFilters?: Filter[];
  options?: TotalCountQueryOptions;
  overrideFilterState?: Partial<Record<FilterType, FilterValue>>;
  disableFilterStateForComparisonSegment?: boolean;
};

const defaultParams: UseGetHcpTotalCountRequest = {
  hcpBks: [],
  additionalFilters: [],
  overrideFilterState: {},
  options: {
    skip: false,
  },
  disableFilterStateForComparisonSegment: false,
};

export function useGetHcpTotalCount({
  hcpBks = [],
  additionalFilters = [],
  options,
  overrideFilterState = {},
  disableFilterStateForComparisonSegment = false,
}: UseGetHcpTotalCountRequest = defaultParams): UseQueryResult<number, AxiosError> {
  const idFilter: LogicalOrFilter = {
    or: hcpBks.map(hcpBk => ({ member: `${HCP_CUBE_NAME}.hcp_bk`, operator: 'equals', values: [hcpBk] })),
  };

  const globalFilters = useAllowlistedGlobalFilters(
    { includeHco: true },
    overrideFilterState,
    disableFilterStateForComparisonSegment,
  );
  const globalFilterApplied = options?.globalFilterApplied ?? true;

  const filters = [
    {
      and: [idFilter, ...additionalFilters],
    },
  ];

  if (globalFilterApplied)
    filters.push({
      and: globalFilters.and,
    });

  const enabled = !options?.skip;

  const query: CubeQuery = {
    limit: 1,
    measures: [`${HCP_CUBE_NAME}.distinctUciCount`],
    filters,
  };

  return useQuery<CubeRawRow[], AxiosError, number>({
    payload: query,
    options: {
      queryKey: [...HCPKeys.totalCount(query)],
      select: data => toNumberOrNull(data?.[0]?.[`${HCP_CUBE_NAME}.distinctUciCount`]) ?? 0,
      enabled: enabled,
    },
  });
}
